import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';

const LoginModal = ({ onLogin, onClose }) => {
  const { signIn } = useAuth();
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      await signIn(credentials?.email, credentials?.password);
      onLogin();
    } catch (error) {
      setErrors({
        general: error?.message || 'Invalid credentials. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-premium-xl w-full max-w-md border border-border">
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Shield" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground">Admin Login</h2>
                <p className="text-sm text-muted-foreground">ORAZEK Investment</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {errors?.general && (
            <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error whitespace-pre-line">{errors?.general}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              label="Email Address"
              placeholder="Enter your email"
              value={credentials?.email}
              onChange={(e) => setCredentials({ ...credentials, email: e?.target?.value })}
              required
              error={errors?.email}
            />

            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                label="Password"
                placeholder="Enter your password"
                value={credentials?.password}
                onChange={(e) => setCredentials({ ...credentials, password: e?.target?.value })}
                required
                error={errors?.password}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
              </button>
            </div>

            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName="LogIn"
              iconPosition="right"
              className="mt-6"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-2 font-medium">Demo Credentials:</p>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>Admin: admin@orazek.com / Admin@2026</p>
              <p>Manager: manager@orazek.com / Manager@2026</p>
              <p>Staff: staff@orazek.com / Staff@2026</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;